import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";
import {
  Observable,
  catchError,
  finalize,
  map,
  shareReplay,
  tap,
  throwError,
} from "rxjs";
import { environment } from "../../environments/environment";
import { OpportunityPersistenceGuardService } from "../pages/opportunities/services/opportunity-persistence-guard.service";
import { ScreenLockService } from "./screen-lock.service";

export interface StaffUser {
  id: string;
  name: string;
  username: string;
  email: string;
  role: string;
  department: string;
  mobileNumber: string;
  employeeId?: string;
  userCode?: string;
  roleName?: string;
  roleLabel?: string;
  reportingTo?: string;
  allBranchesAccess?: boolean;
  branchId?: string;
  branch?: string;
  branchCode?: string;
  dateOfBirth?: string;
  dateOfJoining?: string;
}

interface StaffAuthData {
  accessToken: string;
  refreshToken: string;
  user?: StaffUser;
}

interface StaffAuthResponse {
  success: boolean;
  message: string;
  data: StaffAuthData;
}

const ACCESS_TOKEN_KEY = "los_access_token";
const REFRESH_TOKEN_KEY = "los_refresh_token";
const USER_KEY = "los_user";
/**
 * Stored in localStorage only (never sessionStorage) so the preference
 * survives page refreshes within the same browser session.
 * Value: 'local' → use localStorage; 'session' → use sessionStorage.
 * Absent (e.g. existing sessions) → defaults to localStorage for backward compat.
 */
const STORAGE_TYPE_KEY = "los_storage_type";
/**
 * Persists the user's "Remember me" choice across logouts so the checkbox
 * can be pre-checked on their next visit to the login page.
 * Intentionally NOT cleared in clearSession().
 */
const REMEMBER_ME_PREF_KEY = "los_remember_me_pref";

@Injectable({ providedIn: "root" })
export class AuthService {
  private refreshInFlight$: Observable<boolean> | null = null;
  private readonly opportunityPersistence = inject(
    OpportunityPersistenceGuardService,
  );
  private readonly screenLock = inject(ScreenLockService);

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
  ) {}

  login(login: string, password: string, rememberMe = false): Observable<StaffAuthResponse> {
    this.clearSession();
    // Record the storage preference before the response arrives so that
    // storeSession() (called in the tap) already writes to the right storage.
    localStorage.setItem(STORAGE_TYPE_KEY, rememberMe ? "local" : "session");
    // Persist the choice independently of the session so the checkbox can be
    // pre-checked on the next visit to the login page.
    localStorage.setItem(REMEMBER_ME_PREF_KEY, String(rememberMe));
    return this.http
      .post<StaffAuthResponse>(`${environment.apiUrl}/staff-auth/login`, {
        login,
        password,
        rememberMe,
      })
      .pipe(tap((response) => this.storeSession(response.data)));
  }

  /** Returns the last "Remember me" choice the user made, defaulting to false. */
  getRememberMePreference(): boolean {
    return localStorage.getItem(REMEMBER_ME_PREF_KEY) === "true";
  }

  /** Verifies password without clearing the current session (lock screen unlock). */
  reauthenticate(
    login: string,
    password: string,
  ): Observable<StaffAuthResponse> {
    return this.http
      .post<StaffAuthResponse>(`${environment.apiUrl}/staff-auth/login`, {
        login,
        password,
      })
      .pipe(tap((response) => this.storeSession(response.data)));
  }

  /**
   * Refresh LMS staff tokens via LOS BFF. Shares one in-flight request so
   * concurrent 401s / guard checks do not stampede the refresh endpoint.
   */
  refreshSession(): Observable<boolean> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error("Missing refresh token"));
    }

    this.refreshInFlight$ ??= this.http
      .post<StaffAuthResponse>(`${environment.apiUrl}/staff-auth/refresh`, {
        refreshToken,
      })
      .pipe(
        tap((response) => this.storeSession(response.data)),
        map(() => true),
        catchError((error) => {
          this.clearSession();
          return throwError(() => error);
        }),
        finalize(() => {
          this.refreshInFlight$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );

    return this.refreshInFlight$;
  }

  /**
   * Returns the storage that holds the current session tokens.
   * Defaults to localStorage for backward compatibility with existing sessions
   * that pre-date the Remember Me flag.
   */
  private getTokenStorage(): Storage {
    return localStorage.getItem(STORAGE_TYPE_KEY) === "session"
      ? sessionStorage
      : localStorage;
  }

  getAccessToken(): string | null {
    return this.getTokenStorage().getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return this.getTokenStorage().getItem(REFRESH_TOKEN_KEY);
  }

  currentUser(): StaffUser | null {
    const raw = this.getTokenStorage().getItem(USER_KEY);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as StaffUser;
    } catch {
      return null;
    }
  }

  displayName(): string {
    const user = this.currentUser();
    return user?.username?.trim() ?? user?.name?.trim() ?? "User";
  }

  displayEmail(): string {
    return this.currentUser()?.email?.trim() ?? "";
  }

  displayEmployeeId(): string {
    const user = this.currentUser();
    return (
      user?.employeeId?.trim() ??
      user?.userCode?.trim() ??
      ""
    );
  }

  isAccessTokenExpired(skewMs = 30_000): boolean {
    return this.isJwtExpired(this.getAccessToken(), skewMs);
  }

  isRefreshTokenExpired(skewMs = 0): boolean {
    return this.isJwtExpired(this.getRefreshToken(), skewMs);
  }

  private isJwtExpired(token: string | null, skewMs: number): boolean {
    if (!token) {
      return true;
    }
    const expMs = this.readTokenExpiryMs(token);
    if (expMs == null) {
      return false;
    }
    return Date.now() >= expMs - skewMs;
  }

  isAuthenticated(): boolean {
    return this.hasValidSession() && !this.isAccessTokenExpired(0);
  }

  /** Ensures stored user profile matches the signed-in access token subject. */
  hasValidSession(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return false;
    }
    const claims = this.decodeAccessClaims(token);
    const user = this.currentUser();
    if (!claims?.sub || !user?.id) {
      return false;
    }
    return claims.sub === user.id;
  }

  /** Clears storage and navigates to login. */
  logout(): void {
    void this.opportunityPersistence.clearOnLogout();
    this.clearSession();
    void this.router.navigateByUrl("/auth/login", { replaceUrl: true });
  }

  clearSession(): void {
    // Wipe from both storages so no stale tokens can survive a storage-type switch.
    for (const storage of [localStorage, sessionStorage]) {
      storage.removeItem(ACCESS_TOKEN_KEY);
      storage.removeItem(REFRESH_TOKEN_KEY);
      storage.removeItem(USER_KEY);
    }
    localStorage.removeItem(STORAGE_TYPE_KEY);
    this.screenLock.clear();
  }

  private storeSession(data: StaffAuthData): void {
    const storage = this.getTokenStorage();
    storage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
    storage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    if (data.user) {
      const existing = this.currentUser();
      storage.setItem(
        USER_KEY,
        JSON.stringify({
          ...existing,
          ...data.user,
          department: data.user.department ?? existing?.department ?? "",
          mobileNumber: data.user.mobileNumber ?? existing?.mobileNumber ?? "",
          employeeId: data.user.employeeId ?? existing?.employeeId,
          userCode: data.user.userCode ?? existing?.userCode,
          roleName: data.user.roleName ?? existing?.roleName,
          roleLabel: data.user.roleLabel ?? existing?.roleLabel,
          reportingTo: data.user.reportingTo ?? existing?.reportingTo,
          allBranchesAccess:
            data.user.allBranchesAccess ?? existing?.allBranchesAccess,
          branchId: data.user.branchId ?? existing?.branchId,
          branch: data.user.branch ?? existing?.branch,
          branchCode: data.user.branchCode ?? existing?.branchCode,
          dateOfBirth: data.user.dateOfBirth ?? existing?.dateOfBirth,
          dateOfJoining: data.user.dateOfJoining ?? existing?.dateOfJoining,
        } satisfies StaffUser),
      );
    } else {
      this.syncUserFromAccessToken(data.accessToken);
    }
    const ownerId =
      this.currentUser()?.id ?? this.decodeAccessClaims(data.accessToken)?.sub;
    void this.opportunityPersistence.ensurePersistenceOwner(ownerId);
  }

  private syncUserFromAccessToken(accessToken: string): void {
    const claims = this.decodeAccessClaims(accessToken);
    if (!claims?.sub) {
      return;
    }
    const existing = this.currentUser();
    this.getTokenStorage().setItem(
      USER_KEY,
      JSON.stringify({
        id: claims.sub,
        name: claims.name ?? existing?.name ?? "",
        username: claims.username ?? existing?.username ?? claims.name ?? "",
        email: claims.email ?? existing?.email ?? "",
        role: claims.role ?? existing?.role ?? "",
        employeeId: claims.employeeId ?? existing?.employeeId,
        roleLabel: claims.roleLabel ?? existing?.roleLabel,
        branch: claims.branch ?? existing?.branch,
        branchCode: claims.branchCode ?? existing?.branchCode,
        branchId: claims.branchId ?? existing?.branchId,
        department: existing?.department ?? "",
        mobileNumber: existing?.mobileNumber ?? "",
        dateOfBirth: existing?.dateOfBirth,
        dateOfJoining: existing?.dateOfJoining,
      } satisfies StaffUser),
    );
  }

  private decodeAccessClaims(token: string): {
    sub?: string;
    email?: string;
    role?: string;
    roleLabel?: string;
    employeeId?: string;
    branch?: string;
    branchCode?: string;
    branchId?: string;
    name?: string;
    username?: string;
  } | null {
    try {
      const payloadPart = token.split(".")[1];
      if (!payloadPart) {
        return null;
      }
      const json = atob(payloadPart.replaceAll("-", "+").replaceAll("_", "/"));
      return JSON.parse(json) as {
        sub?: string;
        email?: string;
        role?: string;
        roleLabel?: string;
        employeeId?: string;
        branch?: string;
        branchCode?: string;
        branchId?: string;
        name?: string;
        username?: string;
      };
    } catch {
      return null;
    }
  }

  private readTokenExpiryMs(token: string): number | null {
    const payload = this.decodeAccessClaims(token);
    const exp = (payload as { exp?: number } | null)?.exp;
    return typeof exp === "number" ? exp * 1000 : null;
  }
}
